/*
 * PER-1218 phase 2 — TS-side sync point for the browser chunk
 * streaming constants.
 *
 * `src/ifc/chunking.rs`'s `CHUNK_TRIANGLE_BUDGET` doc comment
 * explains why these numbers are **provisional**: they come from
 * `docs/web-memory-profile-2026-08-18.md`, which measured up to
 * 32.2 MB / a few million triangles against a working hypothesis of
 * a 1.5 GB per-tab memory budget, and picked 250,000 triangles per
 * chunk as a starting point — not a validated ceiling. Nothing here
 * has been tuned against a real multi-chunk `.rvt` file; no such
 * fixture exists in this environment (see the chunk-manager.ts test
 * seam comment for the full explanation).
 *
 * Rust and this wasm-compiled TS module run in the same tab but
 * don't share memory or a build system, so `CHUNK_TRIANGLE_BUDGET`
 * can't be exported across the wasm boundary — this file is the one
 * place on the TS side these values are allowed to be written. Every
 * other file must import from here, never inline a copy.
 */

/** Must match `src/ifc/chunking.rs`'s `CHUNK_TRIANGLE_BUDGET`. Provisional. */
export const CHUNK_TRIANGLE_BUDGET = 250_000;

/**
 * Provisional per-tab resident-geometry memory budget the chunk
 * manager evicts against. 1.5 GB, matching the working hypothesis in
 * `docs/web-memory-profile-2026-08-18.md`. Not validated against a
 * real multi-chunk file — no such fixture exists yet.
 *
 * PER-1230 blocker 2: this is charged against the *decoded* geometry
 * a resident chunk holds in `three.js` (position/normal/uv/index
 * `TypedArray.byteLength`, summed in `chunk-manager.ts`'s
 * `decodedGeometryBytes`), not the serialized GLB response size. A
 * per-vertex-attribute decode is a small multiple of the wire bytes,
 * so counting the wire size understated real cost and made this
 * ceiling effectively unreachable at production model sizes (measured
 * chunk GLBs top out in the tens of MB — see
 * `docs/web-memory-profile-2026-08-18.md`). Eviction is also no
 * longer *solely* gated on this ceiling — see
 * `LOD_FAR_DISTANCE_FEET`/`LOD_FAR_DISTANCE_FRACTION` and
 * `chunk-manager.ts`'s `evict()`.
 */
export const MEMORY_BUDGET_BYTES = 1.5 * 1024 ** 3;

/**
 * Provisional eviction policy label (documentation/debugging only —
 * the actual behavior lives in `chunk-manager.ts`'s eviction pass):
 * least-recently-used among chunks currently outside the camera
 * frustum. A chunk the camera can currently see is never evicted,
 * even over budget.
 */
export const EVICTION_POLICY = 'lru-per-chunk-outside-frustum' as const;

/**
 * Provisional *floor* (feet) for the distance beyond which a
 * visible-but-far chunk is shown as a cheap AABB-box LOD proxy
 * instead of its real geometry. Chosen relative to this viewer's
 * existing scale references, not measured against a real large
 * model: the default camera's far plane is 2000 (`main.ts`), and the
 * ground grid is 100 units square. Placing the LOD threshold well
 * inside the far plane leaves room to see the proxy before it's
 * clipped, while still being "far" relative to the grid.
 *
 * PER-1230 blocker 1: an *absolute* distance threshold breaks down
 * as models get larger, because `main.ts`'s `frameCameraFromBounds`
 * positions the initial camera *proportionally to the model's own
 * size* (`dist ≈ 1.89 × maxDim`). Past ~350 ft of model width the
 * initial camera is already farther than this floor, so every chunk
 * would be born beyond it and none would ever be requested — a
 * model opens to nothing but wireframe boxes forever. `chunk-manager.ts`'s
 * `setManifest` now computes the *actual* threshold per file as
 * `max(LOD_FAR_DISTANCE_FEET, manifestMaxDim × LOD_FAR_DISTANCE_FRACTION)`,
 * so the threshold scales with the file instead of a fixed guess.
 * This constant remains the floor for small models (keeps existing
 * unit-test expectations and small-file behavior unchanged).
 */
export const LOD_FAR_DISTANCE_FEET = 500;

/**
 * Multiplier applied to a loaded manifest's largest AABB dimension to
 * derive the *actual* per-file LOD-far distance (see
 * `LOD_FAR_DISTANCE_FEET` above). `frameCameraFromBounds` (`main.ts`)
 * places the initial camera at `≈ 1.89 × maxDim` from the model
 * center; this fraction needs enough margin over that so chunks near
 * the far edge of the model (farther from the camera than the
 * center) are still within range on first frame. Provisional, chosen
 * with headroom rather than measured against a real large multi-chunk
 * file (none is reachable in this environment — see
 * `docs/web-memory-profile-2026-08-18.md`).
 */
export const LOD_FAR_DISTANCE_FRACTION = 4;
